import axios from "axios";
import { BaseModel } from "models/common/basemodel";
import { InternalResponse, ServerResponse } from "models/common/responseObjects";
import { CatalogueInsertFunction } from "models/functionModels/catalogues/CatalogueInsertFunction";
import { DispensePricingSearchFunction } from "models/functionModels/dispensePricing/DispensePricingSearchFunction";
import { DispensePricingUpdateFunction } from "models/functionModels/dispensePricing/DispensePricingUpdateFunction";
import { MultiStoreSummaryFunction } from "models/functionModels/multistoreFunctions/MultiStoreSummaryFunction";
import { OptimalBrandUpdateFunction } from "models/functionModels/optimalBrands/OptimalBrandUpdateFunction";
import { CategorySearchFunction } from "models/functionModels/otherFunctions/CategorySearchFunction";
import { DepartmentSearchFunction } from "models/functionModels/otherFunctions/DepartmentSearchFunction";
import { LocationSearchFunction } from "models/functionModels/otherFunctions/LocationSearchFunction";
import { ManufacturerSearchFunction } from "models/functionModels/otherFunctions/ManufacturerSearchFunction";
import { StockGroupSearchFunction } from "models/functionModels/otherFunctions/StockGroupSearchFunction";
import { SubDepartmentSearchFunction } from "models/functionModels/otherFunctions/SubDepartmentSearchFunction";
import { SupplierSearchFunction } from "models/functionModels/otherFunctions/SupplierSearchFunction";
import { StockSearchFunction } from "models/functionModels/stockFunctions/StockSearchFunction";
import { StockUpdateFunction } from "models/functionModels/stockFunctions/StockUpdateFunction";
import { ZFreedomStoreSettingsSearchFunction } from "models/functionModels/ZFSettingsFunctions/ZFreedomStoreSettingsSearchFunction";
import { ZFreedomStoreSettingsUpdateFunction } from "models/functionModels/ZFSettingsFunctions/ZFreedomStoreSettingsUpdateFunction";
import { StoreSummary } from "models/storeModels/MultiStoreGraph";
import { ProductsSpecialPricingExtended } from "models/storeModels/ProductsSpecialPricing";
import { ZFreedomStoreSetting } from "models/storeModels/ZFreedomStoreSettings";

export class StoreFunctionsApi {
    static StockSearch = async (query: StockSearchFunction) => axios.post<ServerResponse<InternalResponse<StockExtended[]>>>(`api/v1/storeFunctions/StockSearch`, query);
    static StockSearchHistory = async (query: MultiStoreSummaryFunction) => axios.get<ServerResponse<StoreSummary>>(`api/v1/storeFunctions/StockSearchHistory`, { params: query });
    static StockUpdate = async (query: StockUpdateFunction) => axios.post<ServerResponse<InternalResponse<boolean>>>(`api/v1/storeFunctions/StockUpdate`, query);
    static CategorySearch = async (query: CategorySearchFunction) => axios.post<ServerResponse<InternalResponse<Category[]>>>(`api/v1/storeFunctions/CategorySearch`, query);
    static DepartmentSearch = async (query: DepartmentSearchFunction) => axios.post<ServerResponse<InternalResponse<Department[]>>>(`api/v1/storeFunctions/DepartmentSearch`, query);
    static LocationSearch = async (query: LocationSearchFunction) => axios.post<ServerResponse<InternalResponse<Location[]>>>(`api/v1/storeFunctions/LocationSearch`, query);
    static ManufacturerSearch = async (query: ManufacturerSearchFunction) => axios.post<ServerResponse<InternalResponse<Manufacturer[]>>>(`api/v1/storeFunctions/ManufacturerSearch`, query);
    static StockGroupSearch = async (query: StockGroupSearchFunction) => axios.post<ServerResponse<InternalResponse<StockGroup[]>>>(`api/v1/storeFunctions/StockGroupSearch`, query);
    static SubDepartmentSearch = async (query: SubDepartmentSearchFunction) => axios.post<ServerResponse<InternalResponse<SubDepartment[]>>>(`api/v1/storeFunctions/SubDepartmentSearch`, query);
    static SupplierSearch = async (query: SupplierSearchFunction) => axios.post<ServerResponse<InternalResponse<Supplier[]>>>(`api/v1/storeFunctions/SupplierSearch`, query);
    static DispensePricingUpdate = async (query: DispensePricingUpdateFunction) => axios.post<ServerResponse<InternalResponse<boolean>>>(`api/v1/storeFunctions/DispensePricingUpdate`, query);
    static DispensePricingSearch = async (query: DispensePricingSearchFunction) => axios.post<ServerResponse<InternalResponse<ProductsSpecialPricingExtended[]>>>(`api/v1/storeFunctions/DispensePricingSearch`, query);
    static OptimalBrandUpdate = async (query: OptimalBrandUpdateFunction) => axios.post<ServerResponse<InternalResponse<boolean>>>(`api/v1/storeFunctions/OptimalBrandUpdate`, query);
    static CatalogueInsert = async (query: CatalogueInsertFunction) => axios.post<ServerResponse<InternalResponse<boolean>>>(`api/v1/storeFunctions/CatalogueInsert`, query);
    static StockSync = async () => axios.post<ServerResponse<InternalResponse<boolean>>>(`api/v1/storeFunctions/StockSync`);
    static ZFreedomSettingSearch = async (query: ZFreedomStoreSettingsSearchFunction) => axios.post<ServerResponse<InternalResponse<ZFreedomStoreSetting>>>(`api/v1/storeFunctions/ZFreedomSettingSearch`, query);
    static ZFreedomSettingUpdate = async (query: ZFreedomStoreSettingsUpdateFunction) => axios.post<ServerResponse<InternalResponse<boolean>>>(`api/v1/storeFunctions/ZFreedomSettingUpdate`, query);
}

export interface Stock extends BaseModel {
    id: string;
    name: string;
    supplierID: string | null;
    active: boolean;
    gSTCode: string;
    isSystem: boolean;
    pBSManufacturerID: string | null;
    robotDrugCode: string;
    isRobotDrug: boolean;
    dispenseShortName: string;
    genericCategoryID: string | null;
    mIMSProductID: string | null;
    scheduleCode: string;
    aPFWarnings: string;
    preferredRank: number | null;
    departmentID: string | null;
    subDepartmentID: string | null;
    manufacturerID: string | null;
    cartonSize: number;
    storeRoomMode: string;
    storeRoomParLevel: number;
    shopSOH: number;
    storeRoomSOH: number;
    minStockLevel: number;
    minOrderQty: number;
    orderStock: boolean;
    authReq: boolean;
    ethical: boolean;
    soldByWeight: boolean;
    discountable: boolean;
    displayDescription: boolean;
    wS1Cost: number | null;
    wS1Qty: number | null;
    wS2Cost: number | null;
    wS2Qty: number | null;
    wS3Cost: number | null;
    wS3Qty: number | null;
    wS4Cost: number | null;
    wS4Qty: number | null;
    wS5Cost: number | null;
    wS5Qty: number | null;
    prevInvCostEx: number;
    lastInvCostEx: number;
    avgCostEx: number;
    costEx: number;
    costLink: string;
    markup: number | null;
    sellPriceEx: number;
    gSTAmount: number;
    sellPrice: number;
    shelfLabel: string | null;
    productLabel: string | null;
    shelfTalker: string | null;
    shelfLabelPaperProfileID: string | null;
    productLabelPaperProfileID: string | null;
    shelfTalkerPaperProfileID: string | null;
    autoPrintShelfLabel: boolean;
    autoPrintProductLabel: boolean;
    autoPrintShelfTalker: boolean;
    defaultShelfPrintQty: number | null;
    defaultShelfPrintMultiplier: number | null;
    defaultProductPrintQty: number | null;
    defaultProductPrintMultiplier: number | null;
    defaultTalkerPrintQty: number | null;
    defaultTalkerPrintMultiplier: number | null;
    lastInvDate: string | null;
    lastSoldDate: string | null;
    created: string;
    createdBy: string;
    lastSaved: string;
    lastSavedBy: string;
    orderNotes: string;
    tillMessage: string;
    isSystemDispense: boolean | null;
    pakDescription: string;
    defaultRepeatReminderInterval: number | null;
    rrp: number | null;
    additionalOrderingInfo: string;
    pBSDiscount: number | null;
    pricingPolicyID: string | null;
    roundingPolicyID: string | null;
    maxStockLevel: number | null;
    onlyOrderWhenBelowMinimum: boolean;
    expectedDiscount: number;
    headOfficeId: number | null;
    isHOLocked: boolean;
    fullName: string;
    height: number | null;
    length: number | null;
    width: number | null;
    weight: number | null;
    volume: number | null;
    maxPurchasesPerCustomer: number | null;
    additionalDetails: string;
    linkStockID: string | null;
    isDepartmentLocked: boolean;
    isOnline: boolean | null;
    doNotSendEScriptRepeat: boolean;
    eScriptAddressOverride: string;
}

export interface StockExtended extends Stock {
    supplierName: string;
    pBSManufacturerName: string;
    genericCategoryName: string;
    departmentName: string;
    subDepartmentName: string;
    manufacturerName: string;
    avgStockValue: number;
    cogs: number;
    gp: number;
    margin: number | null;
    currentPromo: number | null;
    unitsOnOrder: number;
    unitsReceived: number;
    soldQty: number;
    robotSoh: number;
    salesGP: number;
    salesMargin: number | null;
    salesMarkup: number | null;
    salesValue: number;
    salesValueEx: number;
    soh: number;
    stockValue: number;
    turnoverRate: number | null;
    pde: string;
    apn: string;
    categoriesDesc: string;
    locationsDesc: string;
    stockGroupsDesc: string;
}

export interface Category extends BaseModel {
    iD: string;
    name: string;
    created: string;
    createdBy: string;
    lastSaved: string;
    lastSavedBy: string;
    headOfficeId: number | null;
}

export interface Department extends BaseModel {
    iD: string;
    number: string;
    name: string;
    markup: number;
    ethical: boolean;
    applyGST: boolean;
    created: string;
    createdBy: string;
    lastSaved: string;
    lastSavedBy: string;
    discountable: boolean;
    pricingPolicyID: string | null;
    roundingPolicyID: string | null;
    headOfficeId: number | null;
    isLocked: boolean | null;
}

export interface Location extends BaseModel {
    iD: string;
    name: string;
    created: string;
    createdBy: string;
    lastSaved: string;
    lastSavedBy: string;
}

export interface Manufacturer extends BaseModel {
    iD: string;
    code: string;
    name: string;
    created: string;
    createdBy: string;
    lastSaved: string;
    lastSavedBy: string;
    headOfficeId: number | null;
}

export interface StockGroup extends BaseModel {
    iD: string;
    name: string;
    created: string;
    createdBy: string;
    lastSaved: string;
    lastSavedBy: string;
    headOfficeId: number | null;
}

export interface SubDepartment extends BaseModel {
    iD: string;
    name: string;
    number: string;
    deptID: string;
    created: string;
    createdBy: string;
    lastSaved: string;
    lastSavedBy: string;
    headOfficeId: number | null;
    isLocked: boolean | null;
}

export interface Supplier extends BaseModel {
    iD: string;
    name: string;
    contactName: string;
    address: string;
    suburb: string;
    state: string;
    postcode: number | null;
    phoneNumber: string;
    faxNumber: string;
    mobileNumber: string;
    emailAddress: string;
    pharmXSuppID: number | null;
    pharmXOrderCode: string;
    pharmXOrderMethod: string;
    pharmXOrderType: string;
    pharmXOrderBackup: string;
    pharmXOrderGroup: string;
    isSystem: boolean;
    created: string;
    createdBy: string;
    lastSaved: string;
    lastSavedBy: string;
    systemCode: string;
    canBePrimary: boolean;
    zConnectSiteID: number | null;
    headOfficeId: number | null;
    active: boolean;
}